import { FC, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Formik, Form, Field, FieldProps } from 'formik';
import { 
  AiOutlineEye, 
  AiOutlineEyeInvisible, 
  AiOutlineMail, 
  AiOutlineLock,
  AiOutlineUser 
} from 'react-icons/ai';
import { AuthLayout } from '../../../layouts';
import { registerSchema, STAFF_ROLE_OPTIONS } from '../../../../constants';
import { DAV_APIS } from '../../../../adapters';
import { Alert } from '../../../commons';

const Register: FC = () => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const handleRegister = async (values: any) => {
    setLoading(true);
    try {
      const data = {
        email: values.email,
        password: values.password,
        username: `${values.fname} ${values.lname}`,
      };
      await DAV_APIS.auth.register(data, values.role);
      Alert({ message: 'Registered Successfully! Please login.', variant: 'success' });
      setLoading(false);
      navigate('/');
    } catch (error) {
      setLoading(false);
      Alert({ message: 'Failed to register', variant: 'error' });
    }
  };

  return (
    <AuthLayout title='Create Account' subtitle='Join Dav Safaris and start your journey'>
      <Formik
        enableReinitialize
        initialValues={{
          fname: '',
          lname: '',
          email: '',
          role: 'admin',
          password: '',
          confirmPassword: '',
        }}
        validationSchema={registerSchema}
        onSubmit={async (values, { resetForm }) => {
          await handleRegister(values);
          resetForm();
        }}
      >
        {() => (
          <Form className='space-y-4'>
            {/* Name Fields - Side by Side */}
            <div className='grid grid-cols-2 gap-4'>
              {/* First Name */}
              <Field name='fname'>
                {({ field, meta }: FieldProps) => (
                  <div className='space-y-2'>
                    <label 
                      htmlFor='fname' 
                      className='block text-sm font-medium text-gray-700 dark:text-gray-300'
                    >
                      First Name
                    </label>
                    <div className='relative'>
                      <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                        <AiOutlineUser className='h-5 w-5 text-gray-400' />
                      </div>
                      <input
                        {...field}
                        id='fname'
                        type='text'
                        placeholder='John'
                        className={`
                          w-full h-11 pl-10 pr-3 
                          bg-gray-50 dark:bg-gray-800 
                          border rounded-xl
                          text-gray-900 dark:text-white text-sm
                          placeholder:text-gray-400
                          transition-all duration-200
                          focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500
                          ${meta.touched && meta.error ? 
                            'border-red-500' 
                            : 'border-gray-200 dark:border-gray-700'
                          }
                        `}
                      />
                    </div>
                    {meta.touched && meta.error && (
                      <p className='text-xs text-red-500'>{meta.error}</p>
                    )}
                  </div>
                )}
              </Field>

              {/* Last Name */}
              <Field name='lname'>
                {({ field, meta }: FieldProps) => (
                  <div className='space-y-2'>
                    <label 
                      htmlFor='lname' 
                      className='block text-sm font-medium text-gray-700 dark:text-gray-300'
                    >
                      Last Name
                    </label>
                    <div className='relative'>
                      <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                        <AiOutlineUser className='h-5 w-5 text-gray-400' />
                      </div>
                      <input
                        {...field}
                        id='lname'
                        type='text'
                        placeholder='Doe'
                        className={`
                          w-full h-11 pl-10 pr-3 
                          bg-gray-50 dark:bg-gray-800 
                          border rounded-xl
                          text-gray-900 dark:text-white text-sm
                          placeholder:text-gray-400
                          transition-all duration-200
                          focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500
                          ${meta.touched && meta.error ? 
                            'border-red-500' 
                            : 'border-gray-200 dark:border-gray-700'
                          }
                        `}
                      />
                    </div>
                    {meta.touched && meta.error && (
                      <p className='text-xs text-red-500'>{meta.error}</p>
                    )}
                  </div>
                )}
              </Field>
            </div>

            {/* Email Field */}
            <Field name='email'>
              {({ field, meta }: FieldProps) => (
                <div className='space-y-2'>
                  <label 
                    htmlFor='email' 
                    className='block text-sm font-medium text-gray-700 dark:text-gray-300'
                  >
                    Email Address
                  </label>
                  <div className='relative'>
                    <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                      <AiOutlineMail className='h-5 w-5 text-gray-400' />
                    </div>
                    <input
                      {...field}
                      id='email'
                      type='email'
                      autoComplete='email'
                      placeholder='you@example.com'
                      className={`
                        w-full h-11 pl-10 pr-3 
                        bg-gray-50 dark:bg-gray-800 
                        border rounded-xl
                        text-gray-900 dark:text-white text-sm
                        placeholder:text-gray-400
                        transition-all duration-200
                        focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500
                        ${meta.touched && meta.error ? 
                          'border-red-500' 
                          : 'border-gray-200 dark:border-gray-700'
                        }
                      `}
                    />
                  </div>
                  {meta.touched && meta.error && (
                    <p className='text-xs text-red-500'>{meta.error}</p>
                  )}
                </div>
              )}
            </Field>

            {/* Role Field */}
            <Field name='role'>
              {({ field, meta }: FieldProps) => (
                <div className='space-y-2'>
                  <label
                    htmlFor='role'
                    className='block text-sm font-medium text-gray-700 dark:text-gray-300'
                  >
                    Role
                  </label>
                  <select
                    {...field}
                    id='role'
                    className={`
                      w-full h-11 pl-3 pr-3
                      bg-gray-50 dark:bg-gray-800
                      border rounded-xl
                      text-gray-900 dark:text-white text-sm
                      transition-all duration-200
                      focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500
                      ${meta.touched && meta.error ?
                        'border-red-500'
                        : 'border-gray-200 dark:border-gray-700'
                      }
                    `}
                  >
                    {STAFF_ROLE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  {meta.touched && meta.error && (
                    <p className='text-xs text-red-500'>{meta.error}</p>
                  )}
                </div>
              )}
            </Field>

            {/* Password Field */}
            <Field name='password'>
              {({ field, meta }: FieldProps) => (
                <div className='space-y-2'>
                  <label 
                    htmlFor='password' 
                    className='block text-sm font-medium text-gray-700 dark:text-gray-300'
                  >
                    Password
                  </label>
                  <div className='relative'>
                    <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                      <AiOutlineLock className='h-5 w-5 text-gray-400' />
                    </div>
                    <input
                      {...field}
                      id='password'
                      type={showPassword ? 'text' : 'password'}
                      autoComplete='new-password'
                      placeholder='••••••••'
                      className={`
                        w-full h-11 pl-10 pr-10 
                        bg-gray-50 dark:bg-gray-800 
                        border rounded-xl
                        text-gray-900 dark:text-white text-sm
                        placeholder:text-gray-400
                        transition-all duration-200
                        focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500
                        ${meta.touched && meta.error ? 
                          'border-red-500' 
                          : 'border-gray-200 dark:border-gray-700'
                        }
                      `}
                    />
                    <button
                      type='button'
                      onClick={() => setShowPassword(!showPassword)}
                      className='absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600'
                    >
                      {showPassword ? (
                        <AiOutlineEyeInvisible className='h-5 w-5' />
                      ) : (
                        <AiOutlineEye className='h-5 w-5' />
                      )}
                    </button>
                  </div>
                  {meta.touched && meta.error && (
                    <p className='text-xs text-red-500'>{meta.error}</p>
                  )}
                </div>
              )}
            </Field>

            {/* Confirm Password Field */}
            <Field name='confirmPassword'>
              {({ field, meta }: FieldProps) => (
                <div className='space-y-2'>
                  <label 
                    htmlFor='confirmPassword' 
                    className='block text-sm font-medium text-gray-700 dark:text-gray-300'
                  >
                    Confirm Password
                  </label>
                  <div className='relative'>
                    <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                      <AiOutlineLock className='h-5 w-5 text-gray-400' />
                    </div>
                    <input
                      {...field}
                      id='confirmPassword'
                      type={showConfirmPassword ? 'text' : 'password'}
                      autoComplete='new-password'
                      placeholder='••••••••'
                      className={`
                        w-full h-11 pl-10 pr-10 
                        bg-gray-50 dark:bg-gray-800 
                        border rounded-xl
                        text-gray-900 dark:text-white text-sm
                        placeholder:text-gray-400
                        transition-all duration-200
                        focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500
                        ${meta.touched && meta.error ? 
                          'border-red-500' 
                          : 'border-gray-200 dark:border-gray-700'
                        }
                      `}
                    />
                    <button
                      type='button'
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className='absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600'
                    >
                      {showConfirmPassword ? (
                        <AiOutlineEyeInvisible className='h-5 w-5' />
                      ) : (
                        <AiOutlineEye className='h-5 w-5' />
                      )}
                    </button>
                  </div>
                  {meta.touched && meta.error && (
                    <p className='text-xs text-red-500'>{meta.error}</p>
                  )}
                </div>
              )}
            </Field>

            {/* Submit Button */}
            <button
              type='submit'
              disabled={loading}
              style={{
                background: 'linear-gradient(to right, #10B981, #059669)',
              }}
              className='
                w-full h-12 mt-2
                text-white font-semibold
                rounded-xl
                shadow-lg
                transition-all duration-200
                hover:opacity-90
                hover:-translate-y-0.5
                active:translate-y-0
                disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0
                flex items-center justify-center gap-2
              '
            >
              {loading ? (
                <>
                  <svg 
                    className='animate-spin h-5 w-5' 
                    viewBox='0 0 24 24'
                    fill='none'
                  >
                    <circle 
                      className='opacity-25' 
                      cx='12' 
                      cy='12' 
                      r='10' 
                      stroke='currentColor' 
                      strokeWidth='4'
                    />
                    <path 
                      className='opacity-75' 
                      fill='currentColor' 
                      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                    />
                  </svg>
                  <span>Creating account...</span>
                </>
              ) : (
                'Create Account'
              )}
            </button>

            {/* Divider */}
            <div className='relative my-4'>
              <div className='absolute inset-0 flex items-center'>
                <div className='w-full border-t border-gray-200 dark:border-gray-700' />
              </div>
              <div className='relative flex justify-center'>
                <span className='px-4 bg-white dark:bg-gray-900 text-sm text-gray-500'>
                  Already have an account?
                </span>
              </div>
            </div>

            {/* Login Link */}
            <Link
              to='/'
              className='
                w-full h-12 
                flex items-center justify-center
                border-2 border-primary-500
                text-primary-600 font-semibold
                rounded-xl
                transition-all duration-200
                hover:bg-primary-50 dark:hover:bg-primary-500/10
              '
            >
              Sign In
            </Link>
          </Form>
        )}
      </Formik>
    </AuthLayout>
  );
};

export default Register;
